export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

export interface Venue {
  ID: string;
  Name: string;
  Address: string;
  Subtitle: string;
  Description: string;
  Number: string;
  Availability: Record<DayOfWeek, DayAvailability>;
  PhotoUrls: string[];
  PhotoAltTexts?: Record<string, string>;
  // Video URL for venue
  VideoUrl?: string;
  Rating: number;
  Position: {
    lat: number;
    lng: number;
  };
  Sections: Record<string, Section>;
  PricingRatesPerSection: Record<string, PricingRate>;

  // Overrides for the pricing rate per section by day of week. These take precedence over the PricingRatesPerSection.
  PricingRatesPerSectionPerDayOfWeek: Record<string, Partial<Record<string, PricingRate>>>;

  // Allow overriding between certain start and end times. This could be used for holidays or could be used for an entire month.
  PricingRatesPerSectionOverride: Record<string, PricingRatesPerSectionOverride[]>;

  Attributes: { [key: string]: boolean };

  // Neighborhoods that the venue is in. This is useful for filtering venues by neighborhood.
  Neighborhoods?: string[];

  Enabled: boolean;
  // The email address of the admin who manages this venue. We will email them when a booking is made for this venue.
  AdminEmailsSeparatedByComma: string;

  // The description of the venue that will be displayed on the checkout page. This is useful for things like 
  // "We'll do this and that for free" or "We'll provide this and that for free"
  CheckoutPageDescription: string;

  // Whether the venue has connected to a calendar at least once.
  HasConnectedACalendarOnce: boolean;

  // Additional fees as percentages. This is added to the total price of the booking before tax
  AdditionalFees: AdditionalFee[];

  // Things to know
  ThingsToKnow: string;

  BookingPolicy: string;

  // The name of the person who is hosting the venue. This is useful for things like "Hosted by John Doe"
  HostFirstNameLastInitial: string;

  // Last modified. This is used for SEO.
  LastModified: Date;

  // Whether the venue is viewable on the website. This is useful for venues that are not yet open for booking but we want to keep them in the system for SEO purposes.
  ViewableForInquiry: boolean;

  // IDs of venues that are similar to this venue. This is useful for recommending similar venues to the user.
  SimilarVenueIds: string[];

  // Matterport ID. Present if the venue has a matterport ID and we can show a 3D tour of the venue.
  MatterportID?: string;

  // YouVisit URL. Present if the venue has a YouVisit tour and we can show a 3D tour of the venue.
  YouVisitURL?: string;

  // URLs of files that are available for this venue. Usually their open bar packages like pdfs.
  FileUrls: {
    label: string;
    url: string;
  }[];

  // The email address of the creator of the venue. We use this to track who made the venue
  CreatorEmail: string;

  // The Litty score of the venue. We use this score to sort venues in the search results.
  LittyScore?: number;
  
  // Average amount spent on drinks per person at this venue
  AverageDrinkAmount?: number;

  // Whether the venue is a bar or a raw space. Defaults to true (bar) if not specified.
  // Bars require receipt upload for deposit refunds, raw spaces typically do not.
  IsBar?: boolean;

  // Subscription information
  Subscription?: {
    isActive: boolean;
    tier: 'basic' | 'standard' | 'premium';
    billingPeriod: 'monthly' | 'sixMonth' | 'yearly';
    amount: number;
    startDate: Date;
    endDate: Date;
    nextBillingDate: Date;
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    // For tracking when subscription was last updated
    lastUpdated: Date;
    // For tracking payment status
    paymentStatus: 'active' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'trialing';
    // Cancellation information
    cancelAtPeriodEnd?: boolean;
    canceledAt?: Date;
    cancelReason?: string;
  };
}

// TODO - No one uses this for now. We will implement it later.
export interface AdminPrivateDetails {
  // The email address of the admin who manages this venue. We will email them when a booking is made for this venue.
  AdminEmailsSeparatedByComma: string;
  Number: string;
}

export interface PricingRatesPerSectionOverride {
  StartTime: Date;
  EndTime: Date;
  PricingRate: PricingRate;
}

export interface AdditionalFee {
  name: string;
  type: 'percentage' | 'flat';
  value: number;
  percentage?: number; // Kept for backwards compatibility
  amount?: number; // For backward compatibility with older fee structure
}

export interface DayAvailability {
  // Deprecated field. Kept for backwards compatibility.
  StartTime: Date;
  EndTime: Date;

  // New field to indicate start and end times as a list of times.
  Times?: {
    StartTime: Date;
    EndTime: Date;
  }[];

  IsOpen: boolean;

  // Slots are used to book a specific time slot within the day's availability. This is a booking that is always going to be from the start to end time.
  Slots?: {
    StartTime: Date;
    EndTime: Date;
  }[];
}

export interface Section {
  Name: string;
  Capacity: number;
  Description: string;
  Images: string[];
  OverlapsAllOtherSections: boolean;
  // The description of the section that will be displayed on the checkout page. This is useful for things like 
  // "We'll do this and that for free" or "We'll provide this and that for free"
  CheckoutPageDescription: string;

  // Is parent of specific sections by section ID.
  IsParentOfSectionIDs?: string[];
  
  // Indicates the privacy level of this section
  PrivacyLevel?: 'private' | 'semi-private' | 'non-private';
  
  // Indicates whether spending can be tracked for this section
  CanTrackSpending?: boolean;
  
  // Numeric order for consistent display of sections
  Order?: number;
}

export interface PricingRate {
  RatePerHour: number; // Rate per hour. Can be 0
  RatePerPerson: number; // Rate per person per hour. Can be 0
  AdditionalCostPerPersonForOpenBar: number; // Additional cost per person for open bar. Can be 0
  MinimumPeople: number; // Minimum number of people to add to the booking. Could be 0
  MinimumBookingDuration: number; // Minimum booking duration in hours.
  WeekdayNightMultiplier: number; // Multiplier for weekday night rates. Can be 1 if no extra charge for night time hours.
  WeekdayNightRate?: number; // Rate per hour for weekday night hours. Can be 0 if no extra charge for night time hours.


  FridayAndSaturdayMultiplier: number; // Multiplier for Friday and Saturday rates. Can be 1 if no extra charge for weekend hours.
  FridayAndSaturdayRate?: number; // Rate per hour for Friday and Saturday. Can be 0 if no extra charge for night time hours.

  FridayAndSaturdayNightMultiplier: number; // Multiplier for weekend night rates. Can be 1 if no extra charge for weekend night hours.
  FridayAndSaturdayNightRate?: number; // Rate per hour for Friday and Saturday night hours. Can be 0 if no extra charge for night time hours.


  NightStartHour: number; // Hour at which night starts. Can be 0 if no extra charge for night time hours.
  DepositPercentage: number; // Deposit percentage. Can be 0 if no deposit is required.
  TowardsMinimumSpend: boolean; // If true, this amount is towards the minimum spend.

  // Show "No Minimum Spend Required" on the checkout page. It needs to be above this number of guests and it can't be 0.
  // This can also be triggered by certain weekdays at certain times.
  NoMinimumSpendRequiredAtNumGuests: number;
  NoMinimumSpendTimes?: {
    dayOfWeek: DayOfWeek; // 0-6, where 0 is Monday
    startTime: string; // HH:mm format
    endTime: string; // HH:mm format
  }[];
  
  // Explicitly indicate if this section can be booked with no minimum spend for non-private events
  IsNoMinimumSpendOption?: boolean;
}

export interface Review {
  ID: string;
  VenueID: string;
  ReviewerName: string;
  Rating: number;
  Comment: string;
  CreatedAt: Date | null;
}


export interface VenuePaymentDetails {
  VenueID: string;
  
  SectionPaymentDetails?: Record<string, {
    InvoiceType: 'percentage_of_spend' | 'flat_rate_per_person' | 'flat_rate' | 'flat_rate_per_person_per_hour';
    CommissionRate?: number;
    FlatRateAmount?: number;
    FlatRatePerPerson?: number;
    FlatRatePerPersonPerHour?: number;
    // New field to indicate if we collect payment on behalf of venue for this section
    CollectPaymentForVenue?: boolean;
  }>;

  // For percentage of spend model
  CommissionRate?: number;
  // For flat rate models
  FlatRateAmount?: number;
  // For per-person flat rates
  FlatRatePerPerson?: number;
  // For per-person per-hour flat rates
  FlatRatePerPersonPerHour?: number;
  // Contact email for invoicing (might differ from general admin email)
  InvoiceContactEmail?: string;
  // Stripe customer ID for the venue
  StripeCustomerId?: string;

  // Deprecated StripeConnectAccountID
  StripeConnectAccountID?: string;
  
  // Stripe Connect account ID for direct payments
  StripeConnectAccountId?: string;
  // Whether the Stripe Connect account is fully onboarded and ready to receive payments
  StripeConnectOnboardingComplete?: boolean;
  // New field to indicate if we collect payment on behalf of venue for all sections
  CollectPaymentForVenue?: boolean;
  // Whether this is a test account
  IsTestAccount?: boolean;
  FallbackVenueID?: string; // ID of another venue to use for payments if this venue doesn't have Stripe setup
  FallbackEnabled?: boolean; // Flag to enable/disable using the fallback venue
}

/**
 * Interface representing a venue booking spend report
 */
export interface VenueBookingSpendReport {
  ID: string;
  VenueID: string;
  BookingID: string;
  TotalSpend: number;
  Notes?: string;
  Status: 'submitted' | 'processed' | 'dispute' | 'admin_requested';
  SubmittedAt?: Date;
  ProcessedAt?: Date;
  CreatedAt: Date;
  UpdatedAt: Date;
  // Receipt URLs uploaded by venue admin when submitting spend report
  ReceiptUrls?: string[];
}

export interface VenueInvoice {
  ID: string;
  BookingID: string;
  VenueID: string;
  // If this is present, then this invoice is based on a spend report.
  SpendReportID?: string;
  // If this is present, then this invoice is linked to a venue payment (for commission records)
  PaymentID?: string;
  // Stripe invoice ID
  StripeInvoiceId: string;
  // Internal invoice number for reference
  InvoiceNumber: string;
  Amount: number;
  CalculationMethod: 'percentage_of_spend' | 'flat_rate_per_person' | 'flat_rate' | 'flat_rate_per_person_per_hour';
  CommissionRate?: number;
  BaseAmount?: number;
  // For commission invoices, this tracks how much was paid to the venue
  AmountPaidToVenue?: number;
  NumberOfGuests?: number;
  Status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  DueDate: Date;
  SentAt?: Date;
  PaidAt?: Date;
  // URL to the hosted Stripe invoice
  StripeHostedInvoiceURL?: string;
  // Last Stripe webhook event received
  LastStripeEvent?: string;
  // Last Stripe webhook event timestamp
  LastStripeEventAt?: Date;
  Notes?: string;
  RemindersSent: number;
  LastReminderSentAt?: Date;
  CreatedAt: Date;
  
  // UI display properties - not stored in database
  VenueName?: string;
  BookingDate?: string;
  CustomerName?: string;
}

/**
 * Interface to track payments that need to be sent to venues when we collect payment on their behalf
 */
export interface VenuePayment {
  ID: string;
  VenueID: string;
  BookingID: string;
  BasePrice: number;
  AmountToPay: number;
  CommissionAmount: number;
  CommissionRate: number;
  Status: 'pending' | 'approved' | 'rejected' | 'paid' | 'failed';
  CreatedAt: Date;
  UpdatedAt: Date;
  ProcessedAt?: Date;
  ProcessedBy?: string;
  PaidAt?: Date;
  RejectionReason?: string;
  StripeTransferID?: string;
  StripeConnectReady?: boolean;
  ErrorMessage?: string;
  RetryCount?: number;
  UsedFallbackVenue?: string;
}

// Helper functions for subscription and fee logic
export const VenueSubscriptionHelpers = {
  /**
   * Determines if a venue has an active standard or premium subscription
   */
  hasActiveStandardOrPremiumSubscription: (venue: Venue): boolean => {
    const subscription = venue.Subscription;
    return !!(subscription?.isActive && 
              subscription.paymentStatus === 'active' && 
              (subscription.tier === 'standard' || subscription.tier === 'premium') &&
              new Date(subscription.endDate) > new Date());
  },

  /**
   * Determines if a venue should have the $20 platform fee
   * Fee applies if venue is NOT subscribed to standard/premium OR is only viewable for inquiry
   */
  shouldHavePlatformFee: (venue: Venue): boolean => {
    // If venue is only viewable for inquiry (not enabled for booking), always charge fee
    if (!venue.Enabled && venue.ViewableForInquiry) {
      return true;
    }
    
    // If venue has active standard or premium subscription, no fee
    if (VenueSubscriptionHelpers.hasActiveStandardOrPremiumSubscription(venue)) {
      return false;
    }
    
    // All other cases (no subscription, basic subscription, inactive subscription) require fee
    return true;
  },

  /**
   * Gets the platform fee amount for a venue
   */
  getPlatformFeeAmount: (venue: Venue): number => {
    return VenueSubscriptionHelpers.shouldHavePlatformFee(venue) ? 20 : 0;
  },

  /**
   * Gets the subscription tier for display purposes
   */
  getSubscriptionTier: (venue: Venue): 'premium' | 'standard' | 'basic' | 'none' => {
    const subscription = venue.Subscription;
    
    if (subscription?.isActive && 
        subscription.paymentStatus === 'active') {
      
      // Handle Firestore Timestamp format with seconds/nanoseconds
      let endDate: Date;
      if (subscription.endDate && typeof subscription.endDate === 'object' && 'seconds' in subscription.endDate) {
        endDate = new Date((subscription.endDate as any).seconds * 1000);
      } else {
        endDate = new Date(subscription.endDate);
      }
      
      if (endDate > new Date()) {
        return subscription.tier;
      }
    }
    
    return 'none';
  }
};

export interface User {
  ID: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Number: string;
  SignupDate: Date;
  Type: 'user' | 'guest';
  FromEvent: boolean;
  AdminOfVenueIds?: string[];
  CreatedAt?: Date;
}

export interface Booking {
  ID: string;
  UserID: string;
  UserEmail: string;
  VenueID: string;
  StartTime: Date;
  EndTime: Date;
  SectionID: string;
  NumberOfGuests: number;
  PhoneNumber: string;
  PaymentStatus: 'pending' | 'completed' | 'failed' | 'needs_capture';

  // Booking status goes:
  // pending_for_customer_payment -> [pending_venue_confirmation -> waiting_for_event  / waiting_for_event] -> [cancelled/invoice_sent/spend_report_requested] -> all_payments_received -> completed
  BookingStatus: 'pending_for_customer_payment' | 'pending_venue_confirmation' | 'waiting_for_event' | 'cancelled' | 'invoice_sent' | 'spend_report_requested' | 'all_payments_received' | 'confirmed' | 'pending_venue_payment' | 'alternative_suggested';
  StripeSessionId: string;
  FirstName: string;
  LastName: string;

  // Price breakdown from checkout page. Includes base price, open bar price, and hourly price.
  PriceFromCheckoutPage: {
    basePrice: number;
    fees: AdditionalFee[];
    deposit: number;
    total?: number;
    totalWithProcessingFee?: number;
  };

  // Price we processed from the booking. This is the base price minus the fees and deposit.
  PriceWeProcessed: number;

  // Price we earned from the booking. 
  PriceWeEarned: number;

  // Platform fee charged for this booking (applies to non-subscribed venues)
  PlatformFee?: number;

  // Payment routing information for Stripe Connect integration
  PaymentRouting?: 'venue_direct' | 'platform_only'; // How the payment was routed
  VenueStripeAccountId?: string; // Venue's Stripe Connect account ID if payment was routed to them

  EventID: string;
  CreatedAt: Date;

  // Alternative time fields
  AlternativeStartTime?: string;
  AlternativeEndTime?: string;
  AlternativeSuggested?: boolean;
  AlternativeTimes?: Array<{
    startTime: string;
    endTime: string;
  }>;
  HasMultipleAlternatives?: boolean;
  DenyReason?: string;
  LastUpdatedAt?: Date;
  LastUpdatedBy?: string;
  
  // Fields for when customer accepts an alternative time
  AlternativeAccepted?: boolean;
  AcceptedAlternativeStartTime?: Date;
  AcceptedAlternativeEndTime?: Date;
  MatchMakerSessionID?: string;
  
  // Fields for when customer suggests a new time
  CustomerSuggestedTime?: boolean;
  CustomerTimeMessage?: string;

  // Receipt URLs uploaded by customer after the event
  CustomerReceiptUrls?: string[];
  
  // Coupon code used for this booking
  CouponCode?: string;
}

// Represents a time slot when a venue is booked, without personal booking details.
// This is used to display the availability of a venue. But also to block off time slots that are already booked from other platforms.
export interface VenueBookedTimeSlot {
  ID: string;
  VenueID: string;
  StartTime: Date;
  EndTime: Date;
  SectionID: string; // Optional, to specify which section of the venue is booked

  // The Data of the guess that was used to create this booking. This is useful for debugging and to see how the booking was created.
  GuessData?: {
    SectionName?: string;
    Confidence: number;
    OriginalText: string;
    ReasonForGuess: string;
    OriginalEventID?: string;
    IsManuallyCreated?: boolean; // Flag to indicate this time slot was manually created by an admin
  };
}


export interface EventDetails {
  ID: string;
  VenueID: string;
  SectionID: string;
  CreatorName: string;
  CreatorEmail: string;
  CreatorPhone: string;
  CreatorEventPaymentID: string;
  CreatorEventGuestID: string;
  CapacityAtTimeOfEventCreation: number;
  EventTitle?: string;
  EventDescription?: string;
  EventStartTime: Date;
  EventEndTime: Date;
  TotalPrice: number;
  AmountPaid: number;
  CreatorContribution: number;
  GuestPrice?: number;
  CustomGuestPriceText?: string;
  IsAutoApproveGuests: boolean;
  IsAutoCompletePayment: boolean;
  ExpirationDate: Date;
  Status: 'pending' | 'confirmed' | 'canceled' | 'payment_failed' | 'joinable_without_payment';
  StatusDescription: string;
  UserDisplayNameToEventGuestID: Record<string, string>; // "John J" : "123"
  PendingDisplayNameToEventGuestID: Record<string, string>; // "John J" : "123"
  IsGuestListHidden?: boolean;
  Background?: 'vantafog' | 'pulsing_lines_bg' | 'network_particles_bg';
}

// Server only use. We create this when we create an event and cross reference this when they try to edit the event. 
export interface EventAdminPassword {
  EventID: string;
  Password: string;
}

export interface EventPayment {
  ID: string;
  EventID: string;
  EventGuestID: string;
  Amount: number;
  StripePaymentID: string;
  Status: 'pending' | 'authorized' | 'captured' | 'refunded' | 'will_not_capture';
  CapturedAt?: Date;
  RefundedAt?: Date;
}

export interface EventGuest {
  ID: string;
  EventID: string;
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber?: string;
  Status: 'pending' | 'approved' | 'rejected';
  EventPaymentID?: string;
  IsCreator: boolean;
  // The amount the guest is attempting to pay. Check event payment to see if it was successful or what they actually paid.
  AmountAttemptingToPay: number;
}

export interface EventMessageFromHost {
  ID: string;
  EventID: string;
  Message: string;
  CreatedAt: Date;
  SuccessCount: number;
  FailureCount: number;
  TotalEligibleGuests: number;
}

export interface VenueBookedTimeSlotGuess {
  ID?: string;
  EventID: string;
  VenueID: string;
  StartTime: Date;
  EndTime: Date;
  SectionName?: string;
  Confidence: number;
  OriginalText: string;
  ReasonForGuess: string;
  AlternativeInterpretations?: {
    interpretation: string;
    confidence: number;
  }[];
  NeedsHumanReview: boolean;
  Status: 'pending' | 'confirmed' | 'rejected';
}

export interface VenueGoogleCalendarAccess {
  ID: string;
  VenueID: string;
  AccessToken: string;
  RefreshToken: string;
  ExpiryDate: Date;
  Scope: string;
  // Deprecated
  CalendarID: string;

  // For each calendar, you can select multiple calendars to use.
  SelectedCalendarIDs: string[];
  CalendarIDtoName: Record<string, string>;

  // Custom instructions for AI for this calendar.
  CustomInstructionsForAI: string;
}

// This is the raw event data from the calendar / google calendar API. 
// We use this to process the event and save it as a venue booked time slot.
export interface RawCalendarEvent {
  ID: string;
  VenueID: string;
  Summary: string;
  Description: string;
  StartTime: Date;
  EndTime: Date;
  ColorId: string;
  ICalUID: string;
  EventHash: string;
  EventContents: string;
  EventDate: Date;

  // Reason for not showing this event in the calendar.
  ReasonForNotShowingInCalendar?: string;
}

export interface BarWithEmail {
  Name: string;
  TagVenueLink?: string;
  Emails: string[];
  EmailSent?: boolean;
  // Added timestamp for when the bar was created in the system
  CreatedAt?: any;
  // Added city information
  City?: string;
}

export interface BlogPost {
  ID: string;
  Author: string;
  Title: string;
  Subtitle: string;
  // Blog content, which will be a mix of text and images in order.
  Content: {
    Type: 'text' | 'images' | 'title' | 'button';
    Text?: string;
    StepTitleWithSubtitle?: {
      Title: string;
      Subtitle: string;
      Url?: string;
    };
    ButtonLink?: {
      Text: string;
      Url: string;
      Variant?: 'contained' | 'outlined' | 'text';
      Color?: string;
      Size?: 'small' | 'medium' | 'large';
    };
    ImageUrls?: string[];
    // Map of image URLs to their alt text
    ImageAltTexts?: Record<string, string>;
    ID?: string;
    // Flag to indicate if images in this block are references to venue images
    IsVenueReference?: boolean;
  }[];
  Excerpt: string;
  PublishDate: string;
  LastModified: Date;
  HeadlineImageUrl?: string;
  HeadlineImageAltText?: string;
  Enabled?: boolean; // Whether the blog post is enabled/visible on the site, defaults to true for backward compatibility
  Categories?: string[];
}

export interface NewsLetterSubscriber {
  Email: string;
  Subscribed: boolean;
  SubscribedAt?: Date;
  PageVisited?: string;
}

export interface CustomerInquiry {
  VenueID: string;
  Name: string;
  Email: string;
  Phone: string;
  Budget?: string;
  // Number of guests
  NumberOfGuests?: string;
  Flexibility?: string;
  InterestedInSimilarVenues?: boolean;
  LocationPreference?: string;
  VenueTypePreference?: string;
  Message?: string;
  MustHaves?: string;
  DateRequested: string;
  TimeRequested: string;
  Occasion: string; // Required field for the type of occasion/event
  CreatedAt: Date;
  EstimatedPrice?: number;
}

export interface ChatLogMessage {
  ID: string;
  VenueID: string;

  MatchMakerSessionID: string;

  From: string;
  To: string[];

  // Whether the message is from the venue. This is used for security to make sure no one can impersonate a venue. You can only set this to true if you pass a venue admin inquiry ID
  FromVenue: boolean;


  Message: string;

  // Message without email or phone numbers. If this is present then we will show this one.
  MessageWithoutEmailOrPhoneNumbers?: string;
  CreatedAt: Date;
  // Add new fields for file messages
  FileUrl?: string;
  FileName?: string;
}


export interface MatchMakerSession {
  ID: string;
  Name: string;
  FakeName: string;
  CreatedAt: Date;
  // First one is the initial venue. The rest are the venues that were reached out to after the initial venue.
  VenueIDsReachedOutTo: string[];
  RecommendedVenueIDs: string[];
  GuestCount: number;
  InitialDate: string;
  InitialStartTime: string;
  InitialEndTime: string;
  CustomerEmail: string;
  CustomerPhone: string;
  SubscribedToEmailAlerts: boolean;
  SubscribedToTextAlerts: boolean;
  Status: 'active' | 'inactive' | 'completed' | 'payment_received' | 'processing' | 'recommendations_sent' | 'inquiries_sent';
  PaymentReceivedAt?: Date;

  // Optional fields
  Flexibility?: string;
  LocationPreference?: string;
  VenueTypePreference?: string;
  MustHaves?: string;
  Message?: string;
  Occasion?: string; // Type of occasion/event
  
  // Additional fields used in concierge flow
  CustomerMessage?: string;
  DateRequested?: string;
  TimeRequested?: string;
  AtmospherePreference?: string;
  Budget?: string;
  IsConciergeChatFlow?: boolean;
  IsConciergeInquiry?: boolean;
  PrivateSpaceRequired?: string;
  ConversationState?: 'new' | 'ongoing' | 'complete' | 'inquiries_sent' | 'recommendations_sent';

  // Whether this session is merged with another session. If this is present, then this session is a merged session and you should go to the session with this ID instead.
  SessionMergedWithID?: string;

  // Notes for us to keep track of what we've done for this session.
  AdminNotes?: string;
  AdminNotesLastUpdatedAt?: Date;
  // Tags for categorizing and filtering sessions
  Tags?: string[];
  
  // Additional fields from user query
  InquiriesSentAt?: Date;
  LastUpdated?: Date;
  LastVenueSearchAt?: Date;
  ReadyForVenueSearchAt?: Date;
  SelectedVenueIDs?: string[];

  // Whether the customer has seen the value prop dialog
  HasSeenValuePropDialog?: boolean;
}


// Conversation state enum
export enum ConversationState {
    NEW = 'new',
    ONGOING = 'ongoing',
    COMPLETE = 'complete',
    VENUE_SEARCH_TRIGGERED = 'venue_search_triggered',
    RECOMMENDATIONS_SENT = 'recommendations_sent',
    INQUIRIES_SENT = 'inquiries_sent'
}

export interface AdminInquiry {
  ID: string;
  VenueID: string;
  MatchMakerSessionID: string;
  IsUnavailable: boolean;
  EstimatedPrice?: number;
  UnavailableAt?: Date; // Timestamp when the inquiry was marked as unavailable
  CreatedAt?: Date; // Timestamp when the inquiry was created
  AdminNotes?: string; // Notes added by admin for follow-up context
  LastUpdatedAt?: Date; // Timestamp when the admin notes were last updated
  Tags?: string[]; // Array of tag IDs for categorizing inquiries
}

  // Used to send a message via text or email
export interface MessageToSend {
  To: string;
  Message: string;
  Type: 'email' | 'text';
  Venue: {
    Name: string;
    [key: string]: any;
  };
  RespondLink: string;
  Subject?: string;
  MessageWithoutEmailOrPhoneNumbers?: string;
  Header?: string; // Optional header to display in email templates
}

export interface Offer {
  ID: string;
  MatchMakerSessionID: string;
  VenueID: string;
  SectionID: string;
  Date: string;
  StartTime: string;
  EndTime: string;

  // The price to charge the customer. This is the price after fees and deposit are applied. This is also 
  // the price if its only a deposit off a base price. So if the venue is charing 800, and the deposit is 10%,
  // then the price to charge is 80. 
  PriceToCharge: number;

  // Either a base price with fees and deposit, or no minimum spend
  BasePrice?: number; // Optional since it may be no min spend
  EstimatedPrice?: number; // Optional estimated price that might be lower than the base price
  Fees?: AdditionalFee[]; // Optional fees that apply if exact price is set
  DepositPercentage?: number; // Optional deposit percentage if exact price is set
  DepositAmount?: number; // Optional deposit amount, if this is set its a flat deposit amount
  IsNoMinimumSpend: boolean; // Whether this is a no minimum spend offer
  
  // Space type indicators
  IsPrivateSpace?: boolean; // Whether this offer is for a private space
  IsSemiPrivateSpace?: boolean; // Whether this offer is for a semi-private space
  IsDepositRefundable?: boolean; // Whether the deposit is refundable

  // Fields for handling payment collection for venues
  CollectPaymentForVenue?: boolean; // Whether we're collecting payment on behalf of the venue
  VenuePayoutPercentage?: number; // The percentage of the payment that goes to the venue
  VenuePayoutAmount?: number; // The calculated amount to pay to the venue
  VenuePayoutStatus?: 'pending' | 'processed' | 'failed'; // Status of the payout to the venue
  VenuePayoutDate?: Date; // When the payout was sent to the venue
  VenuePayoutTransactionID?: string; // Stripe payout transaction ID

  // The ID of the booking that was created for this offer
  BookingID?: string;

  // The date and time the offer was paid
  PaymentReceivedAt?: string;

  // The message to send to the guest along with the offer
  Message?: string;
  MessageWithoutEmailOrPhoneNumbers?: string;

  // The date and time the offer was created
  CreatedAt: string;
  
  // Flag indicating that this venue has sent multiple offers to choose from
  HasMultipleOptions?: boolean;
}

export interface HomepageCategory {
  // Id is the city name
  ID: string;
  City: string;
  CategoriesAndVenuesIDsInOrder: {
    Title: string;
    Subtitle: string;
    VenueIDs: string[];
  }[];
}

export interface ConciergeInquiryMessage {
  Name: string;
  Email: string;
  Phone: string;
  Flexibility: string;
  LocationPreference: string;
  VenueTypePreference: string;
  Message: string;
  DateRequested: string;
  TimeRequested: string;
  NumberOfGuests: number;
  CreatedAt: Date;
  Budget: string;
  MustHaves: string;
}

export interface EmailerStats {
  EmailerEmail: string;
  TotalBarsProcessed: number;
  TotalEmailsSent: number;
  TotalVenuesCreated: number;
}

// A text message that was sent from a venue to Elena
export interface MessageToElena {
  ID: string;
  Type: 'sms' | 'email' | 'venue_matchmaker';
  From: string;
  Message: string;
  AssumedVenueID: string;
  AssumedMatchMakerSessionID: string;
  CreatedAt: Date;
  
  // New fields for handling multiple matches
  AlternativeVenueIDs?: string[];
  AlternativeMatchMakerSessionIDs?: string[];
  MostRecentInteractionTime?: Date;
  
  // Status fields
  ProcessedAt?: Date;
  ProcessedAs?: 'venue' | 'customer';
  ProcessingError?: string;


  TrackingInfo?: {
    References: string[];
    InReplyTo: string;
  };
}

export interface ElenaMessageAction {
  ID: string;
  Status: 'pending' | 'completed' | 'failed' | 'processing';
  MatchMakerSessionID: string;
  Recipient: 'customer' | 'venue' | 'support';
  VenueID: string;
  TargetVenueIDs?: string[]; // Array of venue IDs for multi-venue inquiries
  FormOfContact: 'email' | 'text';
  Message: string;
  Offers?: Offer[];
  CreatedAt: Date;
  SentAt?: Date;
  Error?: string; // For storing error messages when delivery fails
  
  // Concierge functionality fields
  ConciergeContinuation?: boolean; // Whether this is part of a concierge conversation flow
  ConversationState?: ConversationState; // Track conversation state for concierge conversations
  ResponseMessage?: string; // Response message from customer
  ParentActionID?: string; // ID of the parent action (for sequences)
  SequenceOrder?: number; // Order in a sequence of actions
  
  // Scheduled message fields
  IsScheduled?: boolean; // Whether this is a scheduled message
  ScheduledFor?: Date; // When this message should be sent
  ScheduleType?: 'follow_up' | 'reminder'; // Type of scheduled message
  LastCustomerActivityCheck?: Date; // Last time we checked for customer activity
  
  // Action type for concierge conversations and other specialized actions
  ActionType?: string; // Type of action, used for concierge actions and other specialized flows
  ExpectsResponse?: boolean; // Whether this action expects a response from the recipient
  
  // Inquiry fields
  SendInquiry?: boolean; // Whether this action should also send an inquiry to the venue
  InquiryError?: string; // For storing error messages when inquiry delivery fails
  ProcessedVenueIDs?: string[]; // Track which venues have been processed in a multi-venue inquiry

}

export interface ElenaVenueNotes {
  ID: string;
  VenueID: string;
  Notes: string;
  CreatedAt: Date;
  LastUpdatedAt: Date;
}

export interface ElenaLearning {
  ID: string;
  EntireChatHistory: string;
  PreviousMessage: string;
  NewMessage: string;
  Learning: string;
  MessageType: 'customer' | 'venue' | 'support';
  MatchMakerSessionID: string;
  CreatedAt: Date;
  GeneratedBy: string; // AI model that generated the learning
}

export interface LimitedAdminInquiry {
  inquiryId: string;
  venueId: string;
  matchMakerSessionId: string;
  isUnavailable: boolean;
  createdAt?: Date;
  date?: string;
  startTime?: string;
  endTime?: string;
  guestCount?: number;
  status?: string;
  isPaymentReceived?: boolean;
  // Information about last communication
  lastMessageInfo?: {
    lastMessageAt: Date;
    isFromVenue: boolean;
    waitingForVenueResponse: boolean;
    daysSinceLastMessage: number;
  };
  hasReceivedOffer?: boolean;
  isPriority?: boolean;
  isStale?: boolean;
  // Section information from offers
  sectionId?: string;
  sectionName?: string;
}

export interface VenueAdminInquirySummary {
  // Document ID format: {venueId}_{inquiryId}
  id: string;
  venueId: string;
  inquiryId: string;
  matchMakerSessionId: string;
  isUnavailable: boolean;
  createdAt: Date;
  lastUpdatedAt: Date;
  
  // Session data (denormalized)
  date: string;
  startTime: string;
  endTime: string;
  guestCount: number;
  customerEmail: string;
  customerPhone: string;
  sessionStatus: string;
  isPaymentReceived: boolean;
  
  // Offer data (denormalized)
  hasReceivedOffer: boolean;
  sectionId?: string;
  sectionName?: string;
  
  // Last message info (denormalized)
  lastMessageInfo?: {
    lastMessageAt: Date;
    isFromVenue: boolean;
    waitingForVenueResponse: boolean;
    daysSinceLastMessage: number;
  };
  
  // Computed flags
  isPriority: boolean;
  isStale: boolean;
  needsFollowUp: boolean;
  hoursSinceLastVenueMessage: number;
  
  // Admin notes
  adminNotes?: string;
  tags?: string[];
}

export interface MessageTemplate {
  ID: string;
  VenueID: string;
  Name: string;
  Content: string;
  CreatedAt: string;
  LastUpdatedAt: string;
  // Optional category to organize templates
  Category?: string;
}
